import { useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp } from 'lucide-react';
import DataTable from '~/components/DataTable';
import ApiErrorComponent from '~/partials/ApiErrorComponent';
import { LevelTags } from '~/partials/Tags';
import { useList } from '~/util/hooks/useList';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import { levelHistoryQuery } from '~/util/queries/public/levels';
import { HistoryEntry } from '~/util/types/changelog';
import { Level } from '~/util/types/levels';

const Route = getRouteApi('/list/$levelid');

const AREDL_CREATION_END_DATE = '2022-08-17';
const PLATFORMER_ADDED_DATE = '2025-05-30';

const groupConsecutiveActions = (data: HistoryEntry[]): HistoryEntry[] => {
    const grouped: HistoryEntry[] = [];
    let tempGroup: HistoryEntry[] = [];

    for (let i = 0; i < data.length; i++) {
        const current = data[i];
        const previous = tempGroup[tempGroup.length - 1];

        if (tempGroup.length === 0 || current.event === previous.event) {
            tempGroup.push(current);
        } else {
            if (tempGroup.length > 1) {
                grouped.push({
                    ...tempGroup[0],
                    grouped: true,
                    event: previous.event,
                    entries: tempGroup,
                    position_diff: tempGroup.reduce(
                        (sum, entry) => sum + entry.position_diff,
                        0,
                    ),
                    cause: undefined,
                });
            } else {
                grouped.push(tempGroup[0]);
            }
            tempGroup = [current];
        }
    }

    if (tempGroup.length > 1) {
        grouped.push({
            ...tempGroup[0],
            grouped: true,
            event: tempGroup[0].event,
            entries: tempGroup,
            position_diff: tempGroup.reduce(
                (sum, entry) => sum + entry.position_diff,
                0,
            ),
            cause: undefined,
        });
    } else if (tempGroup.length === 1) {
        grouped.push(tempGroup[0]);
    }

    return grouped;
};

const HistoryLevelTags = ({ items }: { items: Level[] | undefined }) =>
    items && (
        <LevelTags
            items={items}
            className="p-1 text-secondary-foreground bg-transparent font-semibold clamp-[text-xs-sm-clamp]"
        />
    );

function History({
    groupActions = false,
    hideOtherPlaced = false,
}: {
    groupActions?: boolean;
    hideOtherPlaced?: boolean;
}) {
    const { levelid } = Route.useParams();
    const list = useList();

    const { data, isError, error } = useSuspenseQuery(
        levelHistoryQuery({ list, levelid }),
    );

    const localeDateString = useLocaleDateString();

    const columns: ColumnDef<HistoryEntry>[] = [
        {
            accessorKey: 'position',
            header: 'Position',
            cell: ({ row }) => (
                <p className="text-center">
                    {row.original.status == 'MainList'
                        ? `#${row.original.position}`
                        : '-'}
                </p>
            ),
        },
        {
            accessorKey: 'position_diff',
            header: 'Change',
            cell: ({
                row: {
                    original: { event, position_diff },
                },
            }) =>
                event === 'Placed' ||
                event === 'Pending' ||
                event === 'Removed' ? (
                    ''
                ) : (
                    <>
                        {position_diff < 0 ? (
                            <ArrowUp className="inline text-confirm clamp-[text-xs-sm-clamp]" />
                        ) : (
                            <ArrowDown className="inline text-destructive clamp-[text-xs-sm-clamp]" />
                        )}{' '}
                        {Math.abs(position_diff)}
                    </>
                ),
        },
        {
            accessorKey: 'cause',
            header: 'Cause',
            cell: ({ row: { original: entry } }) => {
                let cause;

                switch (entry.event) {
                    case 'Placed':
                        cause = <>Placed at #{entry.position}</>;
                        if (
                            list == 'classic' &&
                            entry.action_at.split('T')[0] <=
                                AREDL_CREATION_END_DATE
                        ) {
                            cause = <>The AREDL was created ({cause})</>;
                        }
                        if (
                            list == 'platformer' &&
                            entry.action_at.split('T')[0] <=
                                PLATFORMER_ADDED_DATE
                        ) {
                            cause = (
                                <>Platformer was added to the AREDL ({cause})</>
                            );
                        }
                        break;
                    case 'OtherPlaced':
                        cause = !entry?.grouped ? (
                            <>
                                <HistoryLevelTags
                                    items={entry.cause && [entry.cause]}
                                />{' '}
                                was placed above
                            </>
                        ) : (
                            <>
                                {' '}
                                {entry?.entries?.length} levels were placed
                                above
                            </>
                        );
                        break;
                    case 'MovedUp':
                        cause = <>Moved up to #{entry.position}</>;
                        break;
                    case 'MovedDown':
                        cause = (
                            <>
                                {entry.status == 'MainList'
                                    ? `Moved down to #${entry.position}`
                                    : 'Moved to legacy list'}
                            </>
                        );
                        break;
                    case 'Pending':
                        cause = <>Added as pending</>;
                        break;
                    case 'Removed':
                        cause = <>Removed from the list</>;
                        break;
                    case 'OtherMovedUp':
                        cause = !entry?.grouped ? (
                            <>
                                <HistoryLevelTags
                                    items={entry.cause && [entry.cause]}
                                />{' '}
                                was moved above
                            </>
                        ) : (
                            <>
                                {' '}
                                {entry?.entries?.length} levels were moved above
                            </>
                        );
                        break;
                    case 'OtherMovedDown':
                        cause = !entry?.grouped ? (
                            <>
                                <HistoryLevelTags
                                    items={entry.cause && [entry.cause]}
                                />{' '}
                                was moved below
                            </>
                        ) : (
                            <>
                                {' '}
                                {entry?.entries?.length} levels were moved below
                            </>
                        );
                        break;
                    case 'OtherRemoved':
                        cause = !entry?.grouped ? (
                            <>
                                <HistoryLevelTags
                                    items={entry.cause && [entry.cause]}
                                />{' '}
                                was removed from the list
                            </>
                        ) : (
                            <>
                                {' '}
                                {entry?.entries?.length} levels were removed
                                from the list
                            </>
                        );
                        break;
                    default:
                        cause = <>Unknown</>;
                        break;
                }
                return <div className="clamp-[text-xs-sm-clamp]">{cause}</div>;
            },
        },
        {
            accessorKey: 'action_at',
            header: 'Date',
            cell: ({ row }) => (
                <Link
                    from="/list/$levelid"
                    search={(prev) => ({
                        ...prev,
                        time_machine: row.original.action_at,
                    })}
                >
                    {localeDateString(row.original.action_at)}{' '}
                </Link>
            ),
        },
    ];

    let rows: HistoryEntry[] = [];

    if (data) {
        let filtered = data.filter((entry) => {
            return (
                ((entry.event == 'Placed' ||
                    (list == 'classic' &&
                        entry.action_at > AREDL_CREATION_END_DATE) ||
                    (list == 'platformer' &&
                        entry.action_at > PLATFORMER_ADDED_DATE)) &&
                    entry.status !== 'Legacy') ||
                entry.event == 'MovedDown'
            );
        });

        if (hideOtherPlaced) {
            filtered = filtered.filter(
                (entry) => entry.event !== 'OtherPlaced',
            );
        }

        rows = groupActions ? groupConsecutiveActions(filtered) : filtered;
    }

    if (isError) return <ApiErrorComponent {...error} />;
    return (
        <div className="relative w-full">
            <DataTable
                columns={columns}
                data={rows || []}
                headerClassName={() =>
                    'clamp-[text-xs-sm-clamp] px-0.5 lg:p-2 bg-transparent hover:!bg-transparent'
                }
                cellClassName={() => 'px-0.5 py-2 lg:px-2'}
                rowClassName={(row) => {
                    const event = row.original.event;
                    return event === 'MovedUp'
                        ? 'bg-confirm-transparent hover:!bg-confirm-transparent'
                        : event === 'MovedDown' || event === 'Removed'
                          ? 'bg-destructive-transparent hover:!bg-destructive-transparent'
                          : event === 'OtherMovedUp' ||
                              event === 'OtherPlaced' ||
                              event === 'OtherRemoved' ||
                              event === 'OtherMovedDown'
                            ? 'bg-background-light hover:!bg-background-light'
                            : 'bg-transparent hover:!bg-transparent';
                }}
            />
        </div>
    );
}

export default History;
